// =======================================
// DATA TYPES
// =======================================

/**
 * Represents a single time allocation entry in a job card
 */
export interface Allocation {
  date: string;
  employee_name: string;
  employee_type: string | null;
  description: string | null;
  hours: number;
  charge_rate: number;
  total_value: number;
}

/**
 * Represents a single invoice entry in a job card
 */
export interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  /** Date the invoice is attributed to for budgeting/period reporting. */
  recorded_date: string;
  our_fee: number;
  disbursements: number;
  gst_amount: number;
  total_amount: number;
  status: string;
  notes: string | null;
}

/**
 * Represents a complete job card with project details, allocations, and invoices
 */
export interface JobCard {
  project_id: number;
  // The job_cards view overloads project_id to hold the stage's id for stage
  // rows. These fields expose the unambiguous identifiers so consumers can
  // call entity-aware RPCs without having to derive them.
  parent_project_id?: number | null;
  stage_row_id?: number | null;
  project_name: string;
  job_number: string;
  project_lead_name: string;
  client_name: string | null;
  allocations: Array<{
    date: string;
    employee_name: string;
    employee_type: string | null;
    description: string;
    hours: number;
    charge_rate: number;
    total_value: number;
  }>;
  first_allocation_date: string;
  last_allocation_date: string;
  total_hours: number;
  total_cost: number;
  // Invoice data (new fields)
  invoices: Invoice[] | null;
  total_invoiced: number;
  invoice_count: number;
  // Filtered amounts for the selected date range (returned by backend)
  filtered_hours?: number;
  filtered_cost?: number;
  filtered_invoiced?: number;
  // Dynamic status based on filter period
  status?: string | null;
  /** The entity's own workflow status name (a stage row carries the stage's). */
  workflow_status?: string | null;
}

// =======================================
// COMPONENT CONFIGURATION
// =======================================

/**
 * Configuration for table columns in the allocations table
 */
export interface ColumnConfig {
  label: string;
  order: number;
  format?: (value: any) => string;
}

/**
 * Configuration for the allocations table columns
 */
export const allocationColumns: Record<string, ColumnConfig> = {
  date: {
    label: 'Date',
    order: 1,
    format: (value: string) => new Date(value).toLocaleDateString()
  },
  employee_name: {
    label: 'Employee',
    order: 2
  },
  description: {
    label: 'Description',
    order: 3
  },
  hours: {
    label: 'Hours',
    order: 4,
    format: (value: number) => value.toFixed(2)
  },
  charge_rate: {
    label: 'Rate',
    order: 5,
    format: (value: number) => `$${value.toFixed(2)}`
  },
  total_value: {
    label: 'Total',
    order: 6,
    format: (value: number) => `$${value.toFixed(2)}`
  }
};

// =======================================
// STATUS CONFIGURATION
// =======================================

/**
 * Status options for job card filtering
 */
export const JOB_CARD_STATUS_OPTIONS = [
  { value: 'Not Invoiced', label: 'Not Invoiced' },
  { value: 'Invoiced', label: 'Invoiced' }
] as const;

// =======================================
// JOB TYPE CONFIGURATION
// =======================================

/**
 * Job type options for filtering (matches project types)
 */
export const JOB_TYPE_OPTIONS = [
  'Design',
  'Advisory',
  'RWC',
  'RWD'
] as const;

export type JobType = typeof JOB_TYPE_OPTIONS[number];

// =======================================
// SERVER FILTERING CONFIGURATION
// =======================================

/**
 * Filter configuration for server-side filtering
 */
export interface JobCardFilters {
  search_term?: string | null;
  project_lead_filter?: string[] | null;
  date_range_filter?: string | null;
  custom_start_date?: string | null;
  custom_end_date?: string | null;
  status_filter?: string[] | null;
  types_filter?: string[] | null;
  client_filter?: string[] | null;
  project_status_filter?: string[] | null;
  page_size?: number;
  page_offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/**
 * Response structure from server filtering
 */
export interface JobCardListResponse {
  job_cards: JobCard[];
  total_count: number;
  page_info: PaginationInfo;
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_pages: number;
  total_records: number;
  has_next: boolean;
  has_previous: boolean;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

export interface DateRangeOption {
  label: string;
  value: string;
}

/**
 * Default date range options for filtering (matching Invoice Register options)
 */
export const DATE_RANGE_OPTIONS = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Quarter', value: 'thisQuarter' },
  { label: 'This Financial Year', value: 'thisFinancialYear' },
  { label: 'Last Financial Year', value: 'lastFinancialYear' },
  { label: 'All Time', value: 'allTime' },
  { label: 'Custom Range', value: 'custom' }
] as const;

/**
 * Table sorting options
 */
export const SORT_OPTIONS = [
  { label: 'Job Number', value: 'job_number' },
  { label: 'Project Name', value: 'project_name' },
  { label: 'Project Lead', value: 'project_lead_name' },
  { label: 'Total Hours', value: 'total_hours' },
  { label: 'Total Expensed', value: 'total_cost' },
  { label: 'Total Invoiced', value: 'total_invoiced' },
  { label: 'Start Date', value: 'first_allocation_date' },
  { label: 'End Date', value: 'last_allocation_date' }
] as const;

export const DEFAULT_PAGE_SIZE = 20;
export const SEARCH_DEBOUNCE_DELAY = 300;

// =======================================
// DATE UTILITIES
// =======================================

/**
 * Helper function to format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate date ranges for job card presets (matching Invoice Register)
 * Australian Financial Year: July 1 - June 30
 */
export function getJobCardDateRange(preset: string): { from: string; to: string } {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-based (0 = January)
  
  switch (preset) {
    case 'allTime': {
      return {
        from: '',
        to: ''
      };
    }
    
    case 'thisMonth': {
      const startOfMonth = new Date(currentYear, currentMonth, 1);
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      return {
        from: formatDateLocal(startOfMonth),
        to: formatDateLocal(endOfMonth)
      };
    }
    
    case 'lastMonth': {
      const startOfLastMonth = new Date(currentYear, currentMonth - 1, 1);
      const endOfLastMonth = new Date(currentYear, currentMonth, 0);
      return {
        from: formatDateLocal(startOfLastMonth),
        to: formatDateLocal(endOfLastMonth)
      };
    }
    
    case 'thisQuarter': {
      const quarterStart = Math.floor(currentMonth / 3) * 3;
      const startOfQuarter = new Date(currentYear, quarterStart, 1);
      const endOfQuarter = new Date(currentYear, quarterStart + 3, 0);
      return {
        from: formatDateLocal(startOfQuarter),
        to: formatDateLocal(endOfQuarter)
      };
    }
    
    case 'thisFinancialYear': {
      // Australian Financial Year: July 1 - June 30
      const fyStartYear = currentMonth >= 6 ? currentYear : currentYear - 1; // July (month 6) onwards = current FY
      const fyStart = new Date(fyStartYear, 6, 1); // July 1
      const fyEnd = new Date(fyStartYear + 1, 5, 30); // June 30
      return {
        from: formatDateLocal(fyStart),
        to: formatDateLocal(fyEnd)
      };
    }
    
    case 'lastFinancialYear': {
      // Previous Australian Financial Year
      const fyStartYear = currentMonth >= 6 ? currentYear - 1 : currentYear - 2;
      const fyStart = new Date(fyStartYear, 6, 1); // July 1
      const fyEnd = new Date(fyStartYear + 1, 5, 30); // June 30
      return {
        from: formatDateLocal(fyStart),
        to: formatDateLocal(fyEnd)
      };
    }
    
    default: {
      // 'custom' or any other value - return empty (will use custom dates)
      return {
        from: '',
        to: ''
      };
    }
  }
}

// =======================================
// COMPONENT SETTINGS
// =======================================

/**
 * Default settings for the job cards page
 */
export const DEFAULT_SETTINGS = {
  defaultDateRange: 30,
  defaultSort: 'date_desc' as const,
  pageSize: 25,
  currencySymbol: '$',
  dateFormat: 'MM/dd/yyyy',
  dateRangeOptions: [
    { label: 'All Time', value: 'all' },
    { label: 'Last 30 Days', value: 'last30' },
    { label: 'Last 90 Days', value: 'last90' },
    { label: 'Last Year', value: 'lastYear' }
  ] as DateRangeOption[],
} as const; 